export const GAL_TO_IN3 = 231;
export const SHAPE = {
  RECTANGLE: "rectangle",
  SQUARE: "square",
  CYLINDER_VERTICAL: "cylinder_vertical",
  CYLINDER_HORIZONTAL: "cylinder_horizontal",
};
export const SOURCE = {
  ENTERED: "entered",
  CALCULATED: "calculated",
  PREFILLED: "prefilled",
  EXISTING: "existing",
};

function toNumber(value) {
  if (value === "" || value == null) return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return { invalid: true };
  return n;
}

function positive(value) {
  const n = toNumber(value);
  if (n === null) return null;
  if (typeof n === "object") return { invalid: true };
  if (n <= 0) return { invalid: true, nonPositive: true };
  return n;
}

export function validateDimensions(shape, dims) {
  const errors = {};
  const check = (key, label, required) => {
    const v = dims[key];
    if (v === "" || v == null) {
      if (required) errors[key] = `${label} is required`;
      return;
    }
    const n = positive(v);
    if (n && n.invalid) errors[key] = `${label} must be a positive number`;
  };

  if (shape === SHAPE.RECTANGLE) {
    check("length_in", "Length", true);
    check("width_in", "Width", true);
    check("depth_in", "Inside depth", true);
  } else if (shape === SHAPE.SQUARE) {
    check("length_in", "Length", true);
    check("depth_in", "Inside depth", true);
  } else if (shape === SHAPE.CYLINDER_VERTICAL) {
    check("diameter_in", "Diameter", true);
    check("depth_in", "Inside depth", true);
  } else if (shape === SHAPE.CYLINDER_HORIZONTAL) {
    check("diameter_in", "Diameter", true);
    check("length_in", "Length", true);
  }
  return errors;
}

export function grossFromMeasurements(shape, dims) {
  const dimsOk = validateDimensions(shape, dims);
  if (Object.keys(dimsOk).length > 0) {
    return { gallons: null, errors: dimsOk };
  }
  const L = positive(dims.length_in);
  const W = positive(dims.width_in);
  const D = positive(dims.depth_in);
  const dia = positive(dims.diameter_in);

  let gallons;
  if (shape === SHAPE.RECTANGLE) gallons = (L * W * D) / GAL_TO_IN3;
  else if (shape === SHAPE.SQUARE) gallons = (L * L * D) / GAL_TO_IN3;
  else if (shape === SHAPE.CYLINDER_VERTICAL) gallons = (Math.PI * (dia / 2) ** 2 * D) / GAL_TO_IN3;
  else if (shape === SHAPE.CYLINDER_HORIZONTAL) gallons = (Math.PI * (dia / 2) ** 2 * L) / GAL_TO_IN3;
  else return { gallons: null, errors: { shape: "Unknown tank shape" } };

  return { gallons, errors: {} };
}

export function dimensionsFromCapacity(shape, { capacity_gallons, depth_in, length_in, diameter_in, ratio }) {
  const errors = {};
  const gal = positive(capacity_gallons);
  if (gal && gal.invalid) errors.capacity_gallons = "Known capacity must be a positive number";
  if (gal === null) errors.capacity_gallons = "Known capacity is required";

  const r = ratio === "" || ratio == null ? 1 : positive(ratio);
  if (r && r.invalid) errors.ratio = "Ratio must be a positive number";

  let dims = { length_in: "", width_in: "", depth_in: "", diameter_in: "" };

  if (shape === SHAPE.RECTANGLE || shape === SHAPE.SQUARE) {
    const D = positive(depth_in);
    if (D && D.invalid) errors.depth_in = "Inside depth must be a positive number";
    if (D === null) errors.depth_in = "Inside depth is required to estimate dimensions";
    if (Object.keys(errors).length === 0) {
      const area = (gal * GAL_TO_IN3) / D;
      if (shape === SHAPE.SQUARE) {
        const side = Math.sqrt(area);
        dims.length_in = side;
        dims.width_in = side;
        dims.depth_in = D;
      } else {
        const rr = r || 1;
        dims.length_in = Math.sqrt(area * rr);
        dims.width_in = Math.sqrt(area / rr);
        dims.depth_in = D;
      }
    }
  } else if (shape === SHAPE.CYLINDER_VERTICAL) {
    const D = positive(depth_in);
    if (D && D.invalid) errors.depth_in = "Inside depth must be a positive number";
    if (D === null) errors.depth_in = "Inside depth is required to estimate dimensions";
    if (Object.keys(errors).length === 0) {
      dims.diameter_in = 2 * Math.sqrt((gal * GAL_TO_IN3) / (Math.PI * D));
      dims.depth_in = D;
    }
  } else if (shape === SHAPE.CYLINDER_HORIZONTAL) {
    const dia = positive(diameter_in);
    if (dia && dia.invalid) errors.diameter_in = "Diameter must be a positive number";
    if (dia === null) errors.diameter_in = "Diameter is required to estimate length";
    if (Object.keys(errors).length === 0) {
      dims.length_in = (gal * GAL_TO_IN3) / (Math.PI * (dia / 2) ** 2);
      dims.diameter_in = dia;
    }
  } else {
    errors.shape = "Unknown tank shape";
  }

  return { dims, errors };
}

export function usableFromGross(gross, maxFillPct) {
  if (gross == null) return null;
  const pct = maxFillPct === "" || maxFillPct == null ? null : positive(maxFillPct);
  if (pct === null || (pct && pct.invalid)) return gross;
  return gross * Math.min(pct, 100) / 100;
}

export function resolveGeometry({ shape, mode, dims = {}, capacity_gallons, max_fill_pct, ratio }) {
  const errors = {};
  let gross = null;
  let sources = {
    length_in: SOURCE.ENTERED,
    width_in: SOURCE.ENTERED,
    depth_in: SOURCE.ENTERED,
    diameter_in: SOURCE.ENTERED,
    capacity_gallons: SOURCE.CALCULATED,
  };
  let resolved = { ...dims };

  if (mode === "measurements") {
    const out = grossFromMeasurements(shape, dims);
    if (Object.keys(out.errors).length > 0) Object.assign(errors, out.errors);
    gross = out.gallons;
    if (gross != null) sources.capacity_gallons = SOURCE.CALCULATED;
  } else if (mode === "known") {
    const gal = positive(capacity_gallons);
    if (gal === null) errors.capacity_gallons = "Known capacity is required";
    else if (gal && gal.invalid) errors.capacity_gallons = "Known capacity must be a positive number";

    const provided = (key) => dims[key] !== "" && dims[key] != null;
    sources.capacity_gallons = SOURCE.ENTERED;
    resolved.capacity_gallons = gal && !gal.invalid ? gal : "";

    if (provided("length_in")) sources.length_in = SOURCE.ENTERED;
    if (provided("width_in")) sources.width_in = SOURCE.ENTERED;
    if (provided("depth_in")) sources.depth_in = SOURCE.ENTERED;
    if (provided("diameter_in")) sources.diameter_in = SOURCE.ENTERED;

    const needsDerive =
      (shape === SHAPE.RECTANGLE || shape === SHAPE.SQUARE || shape === SHAPE.CYLINDER_HORIZONTAL)
        ? !provided("length_in")
        : shape === SHAPE.CYLINDER_VERTICAL
          ? !provided("diameter_in")
          : true;

    if (Object.keys(errors).length === 0 && needsDerive) {
      const inv = dimensionsFromCapacity(shape, {
        capacity_gallons: gal,
        depth_in: dims.depth_in,
        diameter_in: dims.diameter_in,
        length_in: dims.length_in,
        ratio,
      });
      if (Object.keys(inv.errors).length > 0) {
        Object.assign(errors, inv.errors);
        gross = null;
      } else {
        resolved = { ...resolved, ...inv.dims };
        if (!provided("length_in")) sources.length_in = SOURCE.CALCULATED;
        if (!provided("width_in")) sources.width_in = SOURCE.CALCULATED;
        if (!provided("depth_in") && inv.dims.depth_in) sources.depth_in = SOURCE.CALCULATED;
        if (!provided("diameter_in") && inv.dims.diameter_in) sources.diameter_in = SOURCE.CALCULATED;
        gross = gal;
      }
    } else if (Object.keys(errors).length === 0) {
      const measured = grossFromMeasurements(shape, {
        length_in: dims.length_in,
        width_in: dims.width_in,
        depth_in: dims.depth_in,
        diameter_in: dims.diameter_in,
      });
      if (Object.keys(measured.errors).length > 0) Object.assign(errors, measured.errors);
      gross = gal;
    }
  } else {
    errors.mode = "Choose a capacity mode";
  }

  const usable = usableFromGross(gross, max_fill_pct);
  return {
    values: {
      ...resolved,
      capacity_gallons: gross == null ? "" : round(gross, 1),
      usable_capacity_gallons: usable == null ? "" : round(usable, 1),
    },
    sources,
    errors,
    estimated: mode === "known" || sources.capacity_gallons === SOURCE.CALCULATED,
  };
}

export function round(value, places = 2) {
  if (value == null || !Number.isFinite(value)) return "";
  const f = 10 ** places;
  return Math.round(value * f) / f;
}

export function formatGallons(value) {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return round(n, 1).toFixed(1);
}
